/**
 * First-launch seed. Populates SQLite with sessions, captures, and a
 * lightweight set of sync tasks so the real UI has something to render
 * before the Python backend is wired. Content mirrors the inline mocks
 * inside the hi-fi showcase so the visuals match screens S01 / S11.
 *
 * Every row is validated against the Zod schemas from `@kapture/types`
 * at seed time — if the schema drifts, we'd rather blow up here than
 * ship a broken row into SQLite.
 */
import { Capture, Session, type Capture as CaptureT, type Session as SessionT } from '@kapture/types';
import { all, tx, type Statement } from '@kapture/sync-core';
import { DEFAULT_TEMPLATE_ID } from './templates';
import { platform } from '@kapture/platform';

const OWNER_ID = 'user-sarah-chen';

function iso(offsetMs: number): string {
  return new Date(Date.now() + offsetMs).toISOString();
}

function u(): string {
  // crypto.randomUUID is available in all modern browsers and Node 19+.
  return platform().ids.uuid();
}

async function isEmpty(): Promise<boolean> {
  const rows = await all<{ n: number }>(`SELECT COUNT(*) AS n FROM sessions`);
  return (rows[0]?.n ?? 0) === 0;
}

function buildSeed(): { sessions: SessionT[]; captures: CaptureT[] } {
  const activeSessionId = u();
  const doneASessionId = u();
  const doneBSessionId = u();

  const sessions: SessionT[] = [
    Session.parse({
      id: activeSessionId,
      title: 'Westbourne Bridge — Pier 3',
      templateId: DEFAULT_TEMPLATE_ID,
      status: 'active',
      startLocation: null,
      createdAt: iso(-1000 * 60 * 60 * 2), // 2h ago
      updatedAt: iso(-1000 * 60 * 12), // 12m ago
      submittedAt: null,
      approvedAt: null,
      ownerId: OWNER_ID,
      reviewerId: null,
      meta: { site: 'A40', inspection: 'Bridge v4.2' },
    }),
    Session.parse({
      id: doneASessionId,
      title: 'Elmhurst Overpass — Deck survey',
      templateId: DEFAULT_TEMPLATE_ID,
      status: 'submitted',
      startLocation: null,
      createdAt: iso(-1000 * 60 * 60 * 24 * 1.2), // yesterday
      updatedAt: iso(-1000 * 60 * 60 * 20),
      submittedAt: iso(-1000 * 60 * 60 * 20),
      approvedAt: null,
      ownerId: OWNER_ID,
      reviewerId: null,
      meta: { site: 'M11' },
    }),
    Session.parse({
      id: doneBSessionId,
      title: 'Site walkthrough — Drayton',
      templateId: null,
      status: 'approved',
      startLocation: null,
      createdAt: iso(-1000 * 60 * 60 * 24 * 2), // 2 days ago
      updatedAt: iso(-1000 * 60 * 60 * 24 * 2 + 1000 * 60 * 60 * 3),
      submittedAt: iso(-1000 * 60 * 60 * 24 * 2 + 1000 * 60 * 60 * 3),
      approvedAt: iso(-1000 * 60 * 60 * 24),
      ownerId: OWNER_ID,
      reviewerId: null,
      meta: { site: 'Property 04B' },
    }),
  ];

  const captures: CaptureT[] = [
    // Active session — mirrors S11 Timeline events.
    Capture.parse({
      id: u(),
      sessionId: activeSessionId,
      kind: 'photo',
      blobPath: null, // Phase 2 will write an actual SVG blob here
      byteSize: 0,
      gps: null,
      tags: ['Crack', 'Priority'],
      createdAt: iso(-1000 * 60 * 36),
      updatedAt: iso(-1000 * 60 * 36),
      aiFields: {
        defect: { value: 'Hairline crack', confidence: 0.94 },
      },
      linkedFieldId: null,
      annotations: [],
    }),
    Capture.parse({
      id: u(),
      sessionId: activeSessionId,
      kind: 'voice',
      blobPath: null,
      byteSize: 0,
      gps: null,
      tags: [],
      createdAt: iso(-1000 * 60 * 33),
      updatedAt: iso(-1000 * 60 * 33),
      aiFields: {
        width: { value: '0.3 mm', confidence: 0.94 },
        length: { value: '40 cm', confidence: 0.87 },
      },
      linkedFieldId: null,
      durationMs: 47_000,
      transcript:
        'Pier three, south face. Visible hairline crack running vertically about 0.3 mm wide, extending roughly 40 cm from the waterline.',
    }),
    Capture.parse({
      id: u(),
      sessionId: activeSessionId,
      kind: 'note',
      blobPath: null,
      byteSize: 0,
      gps: null,
      tags: [],
      createdAt: iso(-1000 * 60 * 18),
      updatedAt: iso(-1000 * 60 * 18),
      aiFields: {},
      linkedFieldId: null,
      title: 'Waterline access',
      body: 'Boat needed for pier 3 close-ups. Coordinating with harbormaster for Tuesday slot. Expected tide window 10:30-12:15.',
    }),
    // Submitted session — a couple of rows so counts are non-zero.
    Capture.parse({
      id: u(),
      sessionId: doneASessionId,
      kind: 'photo',
      blobPath: null,
      byteSize: 0,
      gps: null,
      tags: ['Deck'],
      createdAt: iso(-1000 * 60 * 60 * 22),
      updatedAt: iso(-1000 * 60 * 60 * 22),
      aiFields: {},
      linkedFieldId: null,
      annotations: [],
    }),
    Capture.parse({
      id: u(),
      sessionId: doneASessionId,
      kind: 'gps',
      blobPath: null,
      byteSize: 0,
      gps: {
        lat: 51.5142,
        lng: -0.1861,
        accuracyMeters: 4,
        altitudeMeters: 12,
        capturedAt: iso(-1000 * 60 * 60 * 21),
      },
      tags: [],
      createdAt: iso(-1000 * 60 * 60 * 21),
      updatedAt: iso(-1000 * 60 * 60 * 21),
      aiFields: {},
      linkedFieldId: null,
    }),
  ];

  return { sessions, captures };
}

function sessionStatement(s: SessionT): Statement {
  return {
    sql: `INSERT INTO sessions
       (id, title, template_id, status, owner_id, reviewer_id, created_at, updated_at,
        submitted_at, approved_at, meta)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    bind: [
      s.id,
      s.title,
      s.templateId,
      s.status,
      s.ownerId,
      s.reviewerId,
      s.createdAt,
      s.updatedAt,
      s.submittedAt,
      s.approvedAt,
      JSON.stringify(s.meta),
    ],
  };
}

function captureStatement(c: CaptureT): Statement {
  // Store the full discriminated-union payload in `payload_json` so we can
  // round-trip kind-specific fields (note body, voice transcript, etc.).
  const payload = { ...c };
  return {
    sql: `INSERT INTO captures
       (id, session_id, kind, blob_path, byte_size, gps_json, tags_json,
        ai_fields_json, linked_field_id, payload_json, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    bind: [
      c.id,
      c.sessionId,
      c.kind,
      c.blobPath,
      c.byteSize,
      c.gps ? JSON.stringify(c.gps) : null,
      JSON.stringify(c.tags),
      JSON.stringify(c.aiFields),
      c.linkedFieldId,
      JSON.stringify(payload),
      c.createdAt,
      c.updatedAt,
    ],
  };
}

/** Seed on first launch only. Cheap no-op when sessions already exist. */
export async function seedIfEmpty(): Promise<void> {
  if (!(await isEmpty())) return;
  const { sessions, captures } = buildSeed();
  // No seeded sync tasks. There used to be three, so the queue badge read
  // non-zero on first boot before a backend existed — but one of them was a
  // `capture.upsert` carrying `{demo: true}` and a *session* id as its entity
  // id, which against a real server 422s and parks in the queue as a failed
  // task the user cannot act on.
  await tx([...sessions.map(sessionStatement), ...captures.map(captureStatement)]);
}

/** Dev-only: wipe user rows and reseed. Intentionally leaves schema_version alone. */
export async function resetAndSeed(): Promise<void> {
  await tx([
    { sql: 'DELETE FROM sync_tasks' },
    { sql: 'DELETE FROM field_values' },
    { sql: 'DELETE FROM captures' },
    { sql: 'DELETE FROM sessions' },
  ]);
  await seedIfEmpty();
}
